// src/pages/Configuraciones/GestionSaldos.js
import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { 
  FaFileExcel, 
  FaUpload, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaDownload, 
  FaInfoCircle, 
  FaTimesCircle, 
  FaShieldAlt 
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { exportarSaldosCompletosExcel } from '../../utils/saldosExcelExport';
import './GestionSaldos.css';

const GestionSaldos = ({ fraccionamientoId: propFraccionamientoId }) => {
  const { userData } = useAuth();
  const fraccionamientoId = propFraccionamientoId || userData?.fraccionamientoId || userData?.fraccionamientoTd;
  const [archivo, setArchivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [mensajeInfo, setMensajeInfo] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [datosParaImportar, setDatosParaImportar] = useState([]);

  // Estados para Pre-Validación Instantánea local (Pre-flight Check)
  const [preflightResult, setPreflightResult] = useState(null);
  const [paginaPreview, setPaginaPreview] = useState(1);
  const FILAS_POR_PAGINA_PREVIEW = 18;

  const functions = getFunctions();

  // Extractor insensible a mayúsculas, minúsculas, espacios, guiones y acentos
  const extractField = (row, fieldNames) => {
    if (!row || typeof row !== 'object') return null;
    const cleanStr = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_.-]/g, '');

    const targets = fieldNames.map(cleanStr);
    const rowKeys = Object.keys(row);

    for (const key of rowKeys) {
      const cleanKey = cleanStr(key);
      if (targets.includes(cleanKey)) {
        const val = row[key];
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          return val;
        }
      }
    }
    return null;
  };

  // Función de Pre-Validación Instantánea de Saldos en Navegador (1 segundo)
  const validateBalanceRows = (rows) => {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
      return {
        isValid: false,
        errors: [{ fila: 0, campo: 'Archivo', razon: 'El archivo Excel no contiene datos o está vacío.' }],
        previewRows: [],
        totalRows: 0
      };
    }

    const cleanStr = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_.-]/g, '');

    const firstRowKeys = Object.keys(rows[0] || {}).map(cleanStr);
    
    // Encabezados requeridos principales con soporte flexible de nombres
    const hasNumero = firstRowKeys.some(k => ['numero', 'casa', 'num', 'unidad'].includes(k));
    const hasNombre = firstRowKeys.some(k => ['nombre', 'residente', 'nombreresidente', 'propietario'].includes(k));

    if (!hasNumero || !hasNombre) {
      const missing = [];
      if (!hasNumero) missing.push("'numero'");
      if (!hasNombre) missing.push("'nombre'");
      return {
        isValid: false,
        errors: [{
          fila: 1,
          campo: 'Encabezados',
          razon: `Faltan columnas obligatorias: ${missing.join(', ')}. La fila 1 debe incluir 'numero' y 'nombre'.`
        }],
        previewRows: [],
        totalRows: rows.length
      };
    }

    const errors = [];
    const previewRows = [];

    rows.forEach((row, index) => {
      const rowNumber = index + 2; // Fila real en Excel
      const numero = String(extractField(row, ['numero', 'casa', 'num', 'unidad']) || '').trim();
      const nombre = String(extractField(row, ['nombre', 'residente', 'nombre_residente', 'propietario']) || '').trim();
      
      const saldoFavorRaw = extractField(row, ['saldoFavor', 'saldo_favor', 'saldo a favor', 'favor']) ?? 0;
      const deudaTotalRaw = extractField(row, ['deudaTotal', 'deuda_total', 'adeudoTotal', 'adeudo_total', 'adeudo', 'totalAdeudo', 'total']) ?? 0;
      const cuotaManttoRaw = extractField(row, ['cuotaMantto', 'cuota_mantto', 'mantenimiento', 'mantto', 'capital', 'adeudoCapital']) ?? 0;
      const interesTotalRaw = extractField(row, ['interesTotal', 'interestotal', 'intereses', 'interes', 'adeudoInteres', 'adeudoIntereses', 'recargos', 'mora']) ?? 0;
      const extraordinariasRaw = extractField(row, ['extraordinarias', 'cuotasExtraordinarias', 'cuotas_extras', 'extraordinaria', 'extras']) ?? 0;
      const consumosRaw = extractField(row, ['consumos', 'agua', 'consumo', 'servicios']) ?? 0;

      const rowErrors = [];

      if (!numero) rowErrors.push('Campo "numero" de casa está vacío');
      if (!nombre) rowErrors.push('Campo "nombre" de residente está vacío');

      // Convertir valores numéricos limpiando caracteres (comas de miles, MXN y signos)
      const parseAmount = (val) => {
        if (val === null || val === undefined || String(val).trim() === '') return 0;
        if (typeof val === 'number') return Math.abs(val);

        let cleaned = String(val).trim();
        // Quitar comas de miles del formato americano/mexicano
        if (cleaned.includes(',') && cleaned.includes('.')) {
          cleaned = cleaned.replace(/,/g, '');
        } else if (cleaned.includes(',')) {
          cleaned = cleaned.replace(/,/g, '');
        }

        cleaned = cleaned.replace(/[^0-9.-]/g, '');
        const num = Number(cleaned);
        return isNaN(num) ? -1 : Math.abs(num);
      };

      const saldoFavor = parseAmount(saldoFavorRaw);
      const deudaTotal = parseAmount(deudaTotalRaw);
      const cuotaMantto = parseAmount(cuotaManttoRaw);
      const interesTotal = parseAmount(interesTotalRaw);
      const extraordinarias = parseAmount(extraordinariasRaw);
      const consumos = parseAmount(consumosRaw);

      // Extracción y validación de las 10 columnas de Cuotas Extraordinarias (Anual 2017 a Anual 2026)
      const aniosCols = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];
      let sumaPagosAnuales = 0;
      let tieneColumnasAnuales = false;
      const anualidadesMap = {};

      aniosCols.forEach(anio => {
        const valRaw = extractField(row, [`Anual ${anio}`, `anual ${anio}`, `anual${anio}`, String(anio)]);
        if (valRaw !== null && valRaw !== undefined && String(valRaw).trim() !== '') {
          tieneColumnasAnuales = true;
          const parsed = parseAmount(valRaw);
          anualidadesMap[anio] = parsed >= 0 ? parsed : 0;
          sumaPagosAnuales += (parsed >= 0 ? parsed : 0);
        }
      });

      const adeudoAnualidadesEsperado = tieneColumnasAnuales ? Math.max(0, 10000 - sumaPagosAnuales) : 0;
      if (tieneColumnasAnuales && extraordinarias >= 0) {
        // En Agave Azul V, extraordinarias en Columna P debe coincidir con el adeudo de anualidades (o ser mayor si incluye otra cuota como Alberca o Extra 18)
        if (extraordinarias < adeudoAnualidadesEsperado) {
          rowErrors.push(`Incongruencia en cuotas extraordinarias: Columna P 'extraordinarias' ($${extraordinarias}) no puede ser menor a la sumatoria de adeudo de anualidades ($${adeudoAnualidadesEsperado}).`);
        }
      }

      if (saldoFavor < 0) rowErrors.push(`Saldo a favor inválido '${saldoFavorRaw}' (debe ser número sin '$')`);
      if (deudaTotal < 0) rowErrors.push(`Adeudo total inválido '${deudaTotalRaw}' (debe ser número sin '$')`);
      if (extraordinarias < 0) rowErrors.push(`Cuotas extraordinarias inválido '${extraordinariasRaw}'`);
      if (consumos < 0) rowErrors.push(`Consumos inválido '${consumosRaw}'`);

      // REGLA CONTABLE ESTRICTA: No se permite saldoFavor > 0 Y deudaTotal > 0 en la misma vivienda
      if (saldoFavor > 0 && deudaTotal > 0) {
        rowErrors.push(`Incongruencia financiera: No se permite registrar saldo a favor ($${saldoFavor}) y adeudo ($${deudaTotal}) al mismo tiempo en la misma vivienda.`);
      }

      if (rowErrors.length > 0) {
        rowErrors.forEach(err => {
          errors.push({
            fila: rowNumber,
            numero: numero || '—',
            nombre: nombre || '—',
            campo: err.split(' ')[0],
            razon: err
          });
        });
      }

      previewRows.push({
        fila: rowNumber,
        numero: numero || '—',
        nombre: nombre || '—',
        saldoFavor: saldoFavor >= 0 ? saldoFavor : 0,
        deudaTotal: deudaTotal >= 0 ? deudaTotal : 0,
        cuotaMantto: cuotaMantto >= 0 ? cuotaMantto : 0,
        interesTotal: interesTotal >= 0 ? interesTotal : 0,
        extraordinarias: extraordinarias >= 0 ? extraordinarias : 0,
        consumos: consumos >= 0 ? consumos : 0,
        tieneColumnasAnuales,
        sumaPagosAnuales,
        adeudoAnualidades: adeudoAnualidadesEsperado,
        anualidades: anualidadesMap,
        isValid: rowErrors.length === 0,
        errorMsg: rowErrors.join(' | ')
      });
    });

    return {
      isValid: errors.length === 0,
      errors,
      previewRows,
      totalRows: rows.length
    };
  };

  // Generación y descarga del reporte de errores en .txt
  const downloadErrorReport = (errors, fileName) => {
    if (!errors || errors.length === 0) return;
    let content = `======================================================================\n`;
    content += `REPORTE DE ERRORES DE VALIDACIÓN - GESTIÓN DE SALDOS\n`;
    content += `======================================================================\n`;
    content += `Fraccionamiento ID: ${fraccionamientoId || 'Registrado'}\n`;
    content += `Archivo analizado: ${fileName || 'saldos.xlsx'}\n`;
    content += `Fecha de análisis: ${new Date().toLocaleString('es-MX')}\n`;
    content += `Total de errores detectados: ${errors.length}\n`;
    content += `----------------------------------------------------------------------\n\n`;
    content += `DETALLE DE ERRORES POR FILA:\n`;
    content += `----------------------------------------------------------------------\n`;

    errors.forEach(err => {
      content += `• Fila ${err.fila} [Casa ${err.numero} - ${err.nombre}]: ${err.razon}\n`;
    });

    content += `\n======================================================================\n`;
    content += `INSTRUCCIONES DE CORRECCIÓN:\n`;
    content += `1. Abra su archivo de Excel original.\n`;
    content += `2. Revise la regla financiera: Una casa solo puede tener 'saldoFavor > 0' O 'deudaTotal > 0' (no ambos mayores a cero).\n`;
    content += `3. Ingrese los valores numéricos sin signos de pesos '$' ni letras.\n`;
    content += `4. Guarde los cambios y vuelva a seleccionar el archivo en la plataforma.\n`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Reporte_Errores_Saldos_${Date.now()}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Manejar cambio de archivo y ejecutar pre-validación instantánea
  const handleArchivoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const nombreArchivo = file.name.toLowerCase();
      if (!nombreArchivo.endsWith('.xlsx') && !nombreArchivo.endsWith('.xls')) {
        setError('Por favor cargue un archivo Excel (.xlsx o .xls)');
        setArchivo(null);
        setPreflightResult(null);
        return;
      }
      setArchivo(file);
      setError(null);
      setResultado(null);
      setPaginaPreview(1);

      // Pre-validación local instantánea (1 segundo)
      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet);
        const preflight = validateBalanceRows(rows);
        setPreflightResult(preflight);
      } catch (err) {
        console.error('Error pre-validando saldos:', err);
        setPreflightResult({
          isValid: false,
          errors: [{ fila: 1, numero: '—', nombre: '—', campo: 'Archivo', razon: 'El archivo Excel no se pudo leer o está corrupto.' }],
          previewRows: [],
          totalRows: 0
        });
      }
    } else {
      setArchivo(null);
      setPreflightResult(null);
      setPaginaPreview(1);
    }
  };

  // Procesar archivo Excel preparado
  const procesarArchivo = async () => {
    if (!archivo || (preflightResult && !preflightResult.isValid)) {
      setError('Selecciona un archivo Excel válido sin errores de pre-validación.');
      return;
    }

    try {
      setCargando(true);
      setError(null);
      setProgreso(25);

      const arrayBuffer = await archivo.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      setProgreso(50);

      const rows = XLSX.utils.sheet_to_json(worksheet);

      const parseAmount = (val) => {
        if (val === null || val === undefined || String(val).trim() === '') return 0;
        if (typeof val === 'number') return Math.abs(val);

        let cleaned = String(val).trim();
        if (cleaned.includes(',') && cleaned.includes('.')) {
          cleaned = cleaned.replace(/,/g, '');
        } else if (cleaned.includes(',')) {
          cleaned = cleaned.replace(/,/g, '');
        }

        cleaned = cleaned.replace(/[^0-9.-]/g, '');
        const num = Number(cleaned);
        return isNaN(num) ? 0 : Math.abs(num);
      };

      const cleanStr = (s) => String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s_.-]/g, '');

      const datosValidos = rows
        .filter((fila, idx) => {
          const rawNum = extractField(fila, ['numero', 'casa', 'num', 'unidad']);
          if (!rawNum) return false;
          const cleanNum = cleanStr(rawNum);
          if (idx === 0 && (cleanNum === 'numero' || cleanNum === 'casa')) {
            return false;
          }
          return String(rawNum).trim() !== '';
        })
        .map(fila => {
          const anualidades = {};
          [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026].forEach(anio => {
            const rawVal = extractField(fila, [`Anual ${anio}`, `anual ${anio}`, `anual${anio}`, String(anio)]);
            if (rawVal !== null && rawVal !== undefined && String(rawVal).trim() !== '') {
              anualidades[anio] = parseAmount(rawVal);
            }
          });

          return {
            numero: String(extractField(fila, ['numero', 'casa', 'num', 'unidad']) || '').trim(),
            nombre: String(extractField(fila, ['nombre', 'residente', 'nombre_residente', 'propietario']) || '').trim(),
            saldoFavor: parseAmount(extractField(fila, ['saldoFavor', 'saldo_favor', 'saldo a favor', 'favor'])),
            deudaTotal: parseAmount(extractField(fila, ['deudaTotal', 'deuda_total', 'adeudoTotal', 'adeudo_total', 'adeudo', 'totalAdeudo', 'total'])),
            cuotaMantto: parseAmount(extractField(fila, ['cuotaMantto', 'cuota_mantto', 'mantenimiento', 'mantto', 'capital', 'adeudoCapital'])),
            interesTotal: parseAmount(extractField(fila, ['interesTotal', 'interestotal', 'intereses', 'interes', 'adeudoInteres', 'adeudoIntereses', 'recargos', 'mora'])),
            extraordinarias: parseAmount(extractField(fila, ['extraordinarias', 'cuotasExtraordinarias', 'cuotas_extras', 'extraordinaria', 'extras'])),
            consumos: parseAmount(extractField(fila, ['consumos', 'agua', 'consumo', 'servicios'])),
            adeudoExtra18: parseAmount(extractField(fila, ['adeudo_extra_18', 'extra_18', 'extra18', 'adeudoextra18'])),
            ...(Object.keys(anualidades).length > 0 ? { anualidades } : {})
          };
        });

      if (datosValidos.length === 0) {
        setError('No se encontraron filas de saldos válidas en el archivo');
        setCargando(false);
        setProgreso(0);
        return;
      }

      setDatosParaImportar(datosValidos);
      setProgreso(75);
      setMostrarModal(true);
      setCargando(false);
      setProgreso(0);
    } catch (err) {
      setError(`Error al procesar archivo: ${err.message}`);
      setCargando(false);
      setProgreso(0);
    }
  };

  // Confirmar e importar saldos en Firestore / Cloud Function
  const confirmarImportacion = async () => {
    try {
      setCargando(true);
      setProgreso(10);
      setMostrarModal(false);

      const importarSaldosCallable = httpsCallable(functions, 'importarSaldosResidentes');

      setProgreso(50);

      const response = await importarSaldosCallable({
        fraccionamientoId,
        datosResidentes: datosParaImportar
      });

      setProgreso(100);

      if (response.data && response.data.success) {
        setResultado(response.data);
        setArchivo(null);
        setPreflightResult(null);
        setDatosParaImportar([]);
        setError(null);
      } else {
        setError(response.data?.error || 'Error desconocido en la importación');
      }

      setCargando(false);
      setProgreso(0);
    } catch (err) {
      console.error('Error en importación:', err);
      setError(`Error en importación: ${err.message}`);
      setCargando(false);
      setProgreso(0);
    }
  };

  // Exportar plantilla y saldos integrales con 3 hojas especializadas
  const exportarTemplate = async () => {
    try {
      setCargando(true);
      setError(null);
      setMensajeInfo(null);
      setProgreso(10);

      await exportarSaldosCompletosExcel({
        fraccionamientoId,
        db,
        onProgress: (p) => setProgreso(p)
      });

      setMensajeInfo('✓ Plantilla y saldos vigentes descargados exitosamente (Libro Excel con 3 hojas: Saldos, Cuotas Extraordinarias y Resumen Financiero).');
      setCargando(false);
      setProgreso(0);
    } catch (err) {
      console.error('Error exportando plantilla de saldos:', err);
      setError(`Error al descargar plantilla: ${err.message}`);
      setCargando(false);
      setProgreso(0);
    }
  };

  return (
    <div className="gestion-saldos">
      {/* Header premium */}
      <div className="gestion-saldos-header">
        <div className="header-icon-box">
          📊
        </div>
        <div>
          <h1>Gestión de Saldos</h1>
          <p>Importa y exporta plantillas de saldos vigentes de los residentes</p>
        </div>
      </div>

      {/* Guía Visual de Datos Requeridos y Regla Contable */}
      <div className="saldos-guide-container">
        <div className="saldos-guide-card required-card">
          <div className="card-badge-header">
            <span className="badge-pill badge-danger">🔴 Campos Requeridos y Cuotas Extraordinarias</span>
            <p>Estructura de columnas obligatorias en la cabecera (Fila 1):</p>
          </div>
          <ul className="guide-fields-list">
            <li><code>numero</code> - Número o código oficial de la casa (ej. A01, A33).</li>
            <li><code>nombre</code> - Nombre completo del residente titular.</li>
            <li><code>Anual 2017 a Anual 2026 (Cols F a O)</code> - Pagos realizados de las 10 anualidades extraordinarias ($1,000 c/u). Indica cuánto ha abonado el residente en cada año (ej. 1000 = liquidada, 0 = adeudo total, o pago parcial).</li>
            <li><code>extraordinarias (Col P)</code> - Adeudo pendiente de cuotas extraordinarias (fórmula: <code>=10000-SUM(F:O)</code> más cuotas adicionales aprobadas como alberca).</li>
            <li><code>deudaTotal (Col R)</code> - Adeudo consolidado total (<code>cuotaMantto + interesTotal + extraordinarias + consumos</code>).</li>
          </ul>
        </div>

        <div className="saldos-guide-card rule-card">
          <div className="card-badge-header">
            <span className="badge-pill badge-success">🟢 Regla Contable Estricta y Desglose</span>
            <p>Alineación de conceptos y coherencia contable por columna:</p>
          </div>
          <ul className="guide-fields-list">
            <li><code>saldoFavor (Col C)</code> - Saldo a favor (solo válido si la casa no tiene ningún adeudo pendiente).</li>
            <li><code>cuotaMantto (Col D)</code> - Adeudo por cuotas ordinarias de mantenimiento.</li>
            <li><code>interesTotal (Col E)</code> - Recargos moratorios acumulados.</li>
            <li><code>consumos (Col Q)</code> - Adeudo por consumo de agua potable u otros servicios directos.</li>
            <li><code>Conciliación Col P</code> - Las cuotas anuales F a O deben cuadrar exactamente con el adeudo declarado en la columna <code>extraordinarias</code>.</li>
            <li><strong className="rule-alert-text">📌 Prohibido: Bajo ninguna circunstancia se permite registrar saldoFavor mayor a $0 y deudaTotal mayor a $0 al mismo tiempo.</strong></li>
          </ul>
        </div>
      </div>

      {/* Alertas informativas globales */}
      {mensajeInfo && (
        <div className="custom-alert alert-info-box">
          <div className="alert-content">
            <FaInfoCircle className="alert-icon" />
            <span>{mensajeInfo}</span>
          </div>
          <button onClick={() => setMensajeInfo(null)} className="alert-close">✕</button>
        </div>
      )}

      {/* Contenedor principal en Grid */}
      <div className="gestion-saldos-grid">

        {/* Tarjeta 1: Descargar Template */}
        <div className="saldos-card">
          <div className="saldos-card-body">
            <div className="card-emoji-icon green-icon">📥</div>
            <h3>Descargar Template</h3>
            <p>Obtén una plantilla Excel pre-llenada con los saldos actuales de las casas registradas.</p>
            <button
              onClick={exportarTemplate}
              disabled={cargando}
              className="btn-action btn-green"
            >
              <FaDownload /> {cargando ? 'Descargando...' : 'Descargar Template'}
            </button>

            {progreso > 0 && progreso < 100 && (
              <div className="progress-box">
                <div className="progress-bar-bg">
                  <div className="progress-bar-fill" style={{ width: `${progreso}%` }} />
                </div>
                <span>{progreso}% completo</span>
              </div>
            )}
          </div>
        </div>

        {/* Tarjeta 2: Importar Archivo */}
        <div className="saldos-card">
          <div className="saldos-card-body">
            <div className="card-emoji-icon blue-icon">📤</div>
            <h3>Importar Archivo</h3>
            <p>Carga un archivo Excel (.xlsx) con la actualización de saldos.</p>
            
            <label className={`file-upload-dropzone ${archivo ? 'has-file' : ''}`}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleArchivoChange}
                disabled={cargando}
                style={{ display: 'none' }}
              />
              <FaFileExcel className="dropzone-excel-icon" />
              <span>{archivo ? `✅ ${archivo.name}` : 'Selecciona un archivo o arrastra aquí'}</span>
            </label>

            {/* Pre-flight Instant Feedback Card */}
            {preflightResult && (
              <div className={`preflight-box ${preflightResult.isValid ? 'preflight-success' : 'preflight-error'}`}>
                {preflightResult.isValid ? (
                  <div className="preflight-header">
                    <FaCheckCircle className="preflight-icon" />
                    <div>
                      <strong>✓ Archivo Validado Correctamente</strong>
                      <p>{preflightResult.totalRows} registros listos para actualizar saldos.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="preflight-header">
                      <FaExclamationTriangle className="preflight-icon" />
                      <div>
                        <strong>⚠️ Error de Regla Contable ({preflightResult.errors.length} errores)</strong>
                        <p>Corrige las filas indicadas antes de procesar.</p>
                      </div>
                    </div>
                    <div className="preflight-errors-list">
                      {preflightResult.errors.slice(0, 5).map((err, idx) => (
                        <div key={idx} className="preflight-error-item">
                          <span>Fila {err.fila} (Casa {err.numero}): {err.razon}</span>
                        </div>
                      ))}
                      {preflightResult.errors.length > 5 && (
                        <p className="more-errors-text">+{preflightResult.errors.length - 5} errores más detectados...</p>
                      )}
                    </div>
                    <button 
                      type="button" 
                      className="btn-download-report"
                      onClick={() => downloadErrorReport(preflightResult.errors, archivo?.name)}
                    >
                      <FaDownload /> Descargar Reporte de Errores (.txt)
                    </button>
                  </>
                )}
              </div>
            )}

            <button
              onClick={procesarArchivo}
              disabled={!archivo || cargando || (preflightResult && !preflightResult.isValid)}
              className="btn-action btn-blue"
            >
              <FaUpload /> {cargando ? 'Procesando...' : 'Procesar Archivo'}
            </button>
          </div>
        </div>
      </div>

      {/* Vista Previa de Filas Procesadas (Data Preview Table) */}
      {preflightResult && preflightResult.previewRows?.length > 0 && (() => {
        const totalFilas = preflightResult.previewRows.length;
        const totalPaginas = Math.ceil(totalFilas / FILAS_POR_PAGINA_PREVIEW);
        const paginaActual = Math.min(Math.max(1, paginaPreview), totalPaginas);
        const indiceInicio = (paginaActual - 1) * FILAS_POR_PAGINA_PREVIEW;
        const indiceFin = Math.min(indiceInicio + FILAS_POR_PAGINA_PREVIEW, totalFilas);
        const filasPaginadas = preflightResult.previewRows.slice(indiceInicio, indiceFin);

        return (
          <div className="data-preview-container">
            <div className="preview-header">
              <h4>
                <FaShieldAlt /> Vista Previa de Filas Extraídas
                <span className="preview-count-badge">
                  Mostrando {indiceInicio + 1}–{indiceFin} de {totalFilas} registros
                </span>
              </h4>
              <span className="preview-subtitle">
                Revisa el desglose de saldos a favor y adeudos antes de confirmar la actualización.
              </span>
            </div>

            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th title="Número de fila en Excel">#</th>
                    <th title="Número de vivienda">Casa</th>
                    <th title="Nombre del habitante / condómino">Residente</th>
                    <th title="Saldo a Favor disponible" className="text-right">S. Favor</th>
                    <th title="Adeudo Total acumulado" className="text-right">Adeudo Tot.</th>
                    <th title="Cuota de Mantenimiento ordinaria" className="text-right">Mantto.</th>
                    <th title="Intereses o recargos por mora" className="text-right">Intereses</th>
                    <th title="Cuota Extraordinaria pendiente" className="text-right">Extraord.</th>
                    <th title="Consumos de agua u otros cargos" className="text-right">Consumos</th>
                    <th title="Estado de validación contable" className="text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filasPaginadas.map((row, idx) => (
                    <tr key={idx} className={row.isValid ? 'row-valid' : 'row-invalid'}>
                      <td><strong>{row.fila}</strong></td>
                      <td><strong>{row.numero}</strong></td>
                      <td className="cell-residente" title={row.nombre}>{row.nombre}</td>
                      <td className="text-green text-right">${row.saldoFavor.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="text-red text-right font-semibold">${row.deudaTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="text-right">${row.cuotaMantto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className={`text-right ${row.interesTotal > 0 ? 'text-amber font-semibold' : ''}`}>${row.interesTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="text-right">${row.extraordinarias.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="text-right">${row.consumos.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="text-center">
                        {row.isValid ? (
                          <span className="status-badge badge-valid"><FaCheckCircle /> Válido</span>
                        ) : (
                          <span className="status-badge badge-invalid" title={row.errorMsg}><FaTimesCircle /> Incongruencia</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación por lotes de 18 vistas */}
            {totalPaginas > 1 && (
              <div className="preview-pagination">
                <div className="preview-pagination-info">
                  Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong> ({totalFilas} registros en total)
                </div>
                <div className="preview-pagination-actions">
                  <button
                    type="button"
                    className="preview-page-btn"
                    onClick={() => setPaginaPreview(prev => Math.max(1, prev - 1))}
                    disabled={paginaActual === 1}
                  >
                    ◀ Anterior
                  </button>

                  {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      type="button"
                      className={`preview-page-btn page-number ${paginaActual === num ? 'active' : ''}`}
                      onClick={() => setPaginaPreview(num)}
                    >
                      {num}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="preview-page-btn"
                    onClick={() => setPaginaPreview(prev => Math.min(totalPaginas, prev + 1))}
                    disabled={paginaActual === totalPaginas}
                  >
                    Siguiente ▶
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Alerta de Error */}
      {error && (
        <div className="custom-alert alert-error-box">
          <div>
            <h4>❌ Error de Procesamiento</h4>
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)} className="alert-close">✕</button>
        </div>
      )}

      {/* Resultado de Importación Completada */}
      {resultado && (
        <div className="results-container">
          <div className="custom-alert alert-success-box">
            <h3>✅ Importación Completada</h3>
            <p>{resultado.mensaje}</p>
          </div>

          <div className="stats-cards-grid">
            <div className="stat-card blue-card">
              <p>Total Procesadas</p>
              <h2>{resultado.resumen.totalProcesadas}</h2>
            </div>
            <div className="stat-card green-card">
              <p>Actualizadas</p>
              <h2>{resultado.resumen.totalActualizadas}</h2>
            </div>
            <div className="stat-card orange-card">
              <p>Errores</p>
              <h2>{resultado.resumen.totalErrores}</h2>
            </div>
            <div className="stat-card purple-card">
              <p>Tasa de Éxito</p>
              <h2>
                {resultado.resumen.totalProcesadas > 0
                  ? ((resultado.resumen.totalActualizadas / resultado.resumen.totalProcesadas) * 100).toFixed(1)
                  : 0}%
              </h2>
            </div>
          </div>

          {resultado.resumen.detalles && resultado.resumen.detalles.length > 0 && (
            <div className="details-table-section">
              <h4>📋 Detalle de casas actualizadas</h4>
              <div className="preview-table-wrapper">
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Nombre</th>
                      <th>Saldo Favor</th>
                      <th>Adeudo Total</th>
                      <th>Capital</th>
                      <th>Intereses</th>
                      <th>Extraordinarias</th>
                      <th>Consumos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.resumen.detalles.slice(0, 10).map((detalle, idx) => (
                      <tr key={idx}>
                        <td><strong>{detalle.numero}</strong></td>
                        <td>{detalle.nombre}</td>
                        <td className="text-green">${detalle.saldoFavor.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td className="text-red">${detalle.adeudo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td>${detalle.adeudoCapital.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td className={(detalle.adeudoIntereses || detalle.adeudoInteres || 0) > 0 ? 'text-amber font-semibold' : ''}>
                          ${Number(detalle.adeudoIntereses ?? detalle.adeudoInteres ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </td>
                        <td>${(detalle.extraordinarias || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td>${(detalle.consumos || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          <div className="actions-footer">
            <button onClick={() => setResultado(null)} className="btn-secondary">
              Limpiar
            </button>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Actualizar página
            </button>
          </div>
        </div>
      )}

      {/* Modal de Confirmación Personalizado */}
      {mostrarModal && (
        <div className="saldos-modal-overlay">
          <div className="saldos-modal-container">
            <div className="saldos-modal-header">
              <h3>✅ Confirmar Importación de Saldos</h3>
              <button onClick={() => setMostrarModal(false)} className="saldos-modal-close-x">✕</button>
            </div>
            <div className="saldos-modal-body">
              <p style={{ color: '#475569', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                ¿Estás seguro de que deseas procesar y actualizar la información de saldos en la base de datos?
              </p>
              <div style={{
                background: '#dbeafe',
                border: '1px solid #93c5fd',
                color: '#0c4a6e',
                borderRadius: '10px',
                padding: '1rem',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}>
                📊 Total de registros a procesar y actualizar: {datosParaImportar.length} casas
              </div>
            </div>
            <div className="saldos-modal-footer">
              <button onClick={() => setMostrarModal(false)} className="btn-secondary">
                Cancelar
              </button>
              <button onClick={confirmarImportacion} className="btn-primary">
                ✅ Confirmar Importación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionSaldos;
